import type { SagaCond } from './saga.ts';
import type { SendaDef, SendaLine } from './sendas.ts';
import { sec } from './units.ts';

const ln = (id: string, who: string, cond?: SagaCond): SendaLine => (cond ? { id, who, if: cond } : { id, who });

/** The group hasn't met Orla (La Quemadura 1 not played): Brann names her. */
const UNMET: SagaCond = { notFlag: '@quemadura.mapa' };

/** The Atalaya, the map of Aster pinned on its wall (the scenes «en la mesa»); without one, the yard, the map on two barrels. */
const table = (sky: 'night' | 'dusk', cam: { from: [number, number, number]; at: [number, number, number]; ms: number }[], figures: { who: string; q: number; r: number; facing?: number; pose?: 'stand' | 'sit' | 'kneel' | 'walk' }[]) => ({
  place: 'asterTable',
  sky,
  cam,
  figures,
  alt: { unless: 'watchtower' as const, cine: { place: 'asterYard', sky, cam, figures } },
});

/**
 * The ranger's chapter 2 numbers (docs/sendas/explorador.md, «El Acantilado del Flanco»; battle/cliff.ts), SENDA.galo.
 *
 * - `edge` (act 1, the cornice): Sela drives the anchor `time`; her hammer rings every `hammer`; shriekers bite `biteBp` there. Soot crows out of
 *   their nests and shriekers out of the deep cracks, `n` every `every` from `first`.
 * - `wall` (act 2) and `night` (act 3, the test): the rope reaches `rope` (the ranger's base range: more range doesn't
 *   lengthen the rope); a shot takes `aim`; down a rope at `speed` a tick; a crumbling crack holds `hold` with weight,
 *   then gives: who hangs on it swings back to where they shot from, struck `fallBp`. A rope passing within `nest` of a
 *   nest wakes its `crows`; the crew's scared flocks (`crew`) the while. At night: a rope shot, a hollow stone (`stone`,
 *   sounding that long) and the bow ring out; the shriekers (`n` from each deep crack, more with `more` each time) go to
 *   the last sound (`mem` old at most; the bow calls one, too, every `bow` at most; `max` out at once). Swinging onto a
 *   crumbling crack in the dark knocks the hero against the rock (`knockBp`). Their mother (`mother`, `d` seconds of D) keeps off the hero and shrieks every
 *   `every` (the first `first` after she comes): a ring growing `warn` to `r`, stunning `stun` and striking `heroBp`
 *   whoever is in it; while she lives, her cry calls two shriekers out of the crack nearest the hero every `call`.
 * - The shriekers (`shrieker`): their speed, bite (every, reach, % of the hero's life) and blows on Sela.
 * - `hpBp`, `dmgBp`: each act's nightmares over the chapter's common ones.
 */
export const GALO = {
  edge: { time: sec(60), hammer: sec(3), crows: { first: sec(4), every: sec(7), n: 3 }, shriek: { first: sec(7), every: 66, n: 4 }, biteBp: 1700, hpBp: 14000, dmgBp: 26000 },
  wall: { rope: 6000, aim: 6, speed: 220, hold: sec(8), fallBp: 3000, nest: 2400, crows: 4, crew: { first: sec(8), every: sec(9), n: 4 }, hpBp: 13000, dmgBp: 20000 },
  night: {
    rope: 6000,
    aim: 6,
    speed: 220,
    hold: sec(8),
    fallBp: 2500,
    stone: sec(6),
    mem: sec(3),
    crew: { first: sec(5), every: sec(8), n: 4 },
    knockBp: 3500,
    shriek: { n: 2, more: 2, max: 3, bow: sec(2) },
    mother: { d: 90, at: sec(6), first: sec(3), every: sec(9), call: sec(12), warn: sec(3), r: 9000, stun: sec(2), heroBp: 2500, keep: 3000 },
    hpBp: 12000,
    dmgBp: 15000,
  },
  shrieker: { speed: 120, bite: { every: 14, reach: 700, heroBp: 800, sela: sec(3) } },
};

/**
 * The ranger's path, El mapa entero (docs/sendas/explorador.md): Aster, the walker who drew the first map of the
 * Turtle, went down under the shell's rim fifty years ago and never came back. Chapter 1, «Las piedras de Aster»
 * (level 18): Orla's hand on the white patch under the rim (the cinematic); Orla, Sela and the map (the opening); the
 * rim of the Furrows and Aster's cairns, the knots read off their cords (act 1); a fresh notch of a knife on the second
 * cairn (between); the fog in a Furrow, Sela's shout and the echo that comes back from the dead ends (act 2); the
 * campfire under a plate with Sela; and the test, the fallen cairn (the long cord: two rings that cross on a base, the
 * Roller down the slope); closing on the base with a red cord tied that isn't Aster's, and Orla at night: «Galo.
 * Volvió.»
 */
export const RANGER: SendaDef = {
  id: 'ranger',
  cls: 'ranger',
  planned: 5,
  cast: {
    orla: { npc: 'orla' },
    brann: { npc: 'brann' },
    // Sela before Orla names her: «Una chica con dos rollos de soga».
    girl: { model: 'sela' },
    sela: { model: 'sela' },
    // Chapter 2: Galo before Sela names him («Un viejo con una capa roja de andariego»), Galo, his crew, Aster's pages.
    oldman: { model: 'galo' },
    galo: { model: 'galo' },
    crew1: { model: 'galoCrew1' },
    crew2: { model: 'galoCrew2' },
    crew3: { model: 'galoCrew3' },
    aster: { voice: true },
  },
  chapters: [
    {
      id: 'cairns',
      closedBy: 'orla',
      piece: 'knotCord',
      acts: [
        {
          // Aster's map at night: Orla's hand along the shell's rim, stopping on a white patch under it; the sea outside.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-ranger-1',
            place: 'asterMap',
            sky: 'night',
            cam: [
              { from: [-1.6, 3.2, 3.6], at: [0.2, -0.4, 0.2], ms: 6000 },
              { from: [0.6, 1.4, 2.0], at: [0.4, -0.2, 0.2], ms: 6000 },
              { from: [1.0, 0.6, 1.1], at: [0.6, -0.3, 0.25], ms: 5000 },
            ],
            figures: [{ who: 'orla', q: -1, r: 1, facing: 5.2, pose: 'stand' }],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l0', 'brann', UNMET), ln('l1', 'orla'), ln('l2', 'orla'), ln('l3', 'orla', { flag: '@orla.ayuda' }), ln('l4', 'girl'), ln('l5', 'orla'), ln('l6', 'orla'), ln('l7', 'sela'), ln('l8', 'orla')],
          cine: table(
            'night',
            // (The table stands north of the middle: Orla behind it, Sela at its east end, the hero on its near side.)
            [
              { from: [1.0, 3.2, 3.0], at: [1.3, -1.9, 0.4], ms: 8000 },
              { from: [-2.0, -0.4, 1.8], at: [1.6, -2.6, 0.9], ms: 9000 },
              { from: [3.6, 0.4, 1.7], at: [1.3, -1.9, 0.6], ms: 9000 },
            ],
            [
              { who: 'orla', q: 2, r: -3, facing: 2.0, pose: 'stand' },
              { who: 'sela', q: 3, r: -2, facing: 3.0, pose: 'stand' },
              { who: 'hero', q: 0, r: -1, facing: 5.0, pose: 'stand' },
            ],
          ),
        },
        {
          // The rim of the Furrows, live stone and moss over the deep ways; a grey day; cairns all over the clearing.
          kind: 'fight',
          id: 'knots',
          mode: 'cairns',
          place: 'furrowRim',
          budgetBp: 9000,
          goal: { kind: 'cairns' },
          npcs: [{ id: 'sela', does: 'follow', hits: 10, vital: false, safe: true, follow: 1.4 }],
          says: [
            { on: 'start', who: 'sela', line: 'l0', delay: sec(2) },
            { on: 'cue', cue: 'sling', who: 'sela', line: 'l1' },
            { on: 'cue', cue: 'right1', who: 'sela', line: 'l2', delay: sec(1) },
            { on: 'cue', cue: 'wrong1', who: 'sela', line: 'l3' },
            { on: 'cue', cue: 'right2', who: 'sela', line: 'l4', delay: sec(1) },
          ],
        },
        {
          // The second cairn at the lip of a Furrow: in its stone, a fresh notch of a knife.
          kind: 'scene',
          id: 'notch',
          lines: [ln('l1', 'narrator'), ln('l2', 'sela'), ln('l3', 'sela')],
          cine: {
            place: 'furrowLip',
            sky: 'day',
            weather: 'fog',
            cam: [
              { from: [-2.4, 4.2, 2.2], at: [0.2, -0.6, 0.5], ms: 8000 },
              { from: [-0.4, 1.6, 0.9], at: [0.1, -0.2, 0.45], ms: 9000 },
            ],
            figures: [
              { who: 'sela', q: 0, r: 1, facing: 4.6, pose: 'kneel' },
              { who: 'hero', q: 1, r: 1, facing: 4.0, pose: 'stand' },
            ],
          },
        },
        {
          // Inside a Furrow in thick fog: forks of three or four mouths, one goes on (§4, the echo).
          kind: 'fight',
          id: 'fog',
          mode: 'echo',
          place: 'furrowFog',
          weather: 'fog',
          budgetBp: 9000,
          goal: { kind: 'echo' },
          npcs: [{ id: 'sela', does: 'follow', hits: 10, follow: 1.1 }],
          says: [
            { on: 'start', who: 'sela', line: 'l0', delay: sec(2) },
            { on: 'cue', cue: 'shout1', who: 'sela', line: 'l1' },
            { on: 'cue', cue: 'heard1', who: 'sela', line: 'l2', delay: sec(1) },
            { on: 'cue', cue: 'dead1', who: 'sela', line: 'l3' },
            { on: 'cue', cue: 'fork2', who: 'sela', line: 'l4' },
          ],
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['sela'],
          questions: [
            { id: 'counting', lines: [ln('l1', 'sela')], set: ['askedCounting'] },
            { id: 'aster', lines: [ln('l1', 'sela'), ln('l2', 'sela')], set: ['askedAster'] },
            { id: 'sela', lines: [ln('l1', 'sela')], set: ['askedSela'] },
          ],
          cine: {
            place: 'furrowCamp',
            sky: 'night',
            cam: [
              { from: [2.6, 3.0, 2.0], at: [-0.3, -0.3, 0.35], ms: 9000 },
              { from: [-1.6, 3.0, 1.5], at: [0.1, -0.3, 0.4], ms: 9000 },
            ],
            figures: [
              { who: 'sela', q: -1, r: 1, facing: 5.4, pose: 'sit' },
              { who: 'hero', q: 1, r: -1, facing: 2.2, pose: 'sit' },
            ],
            fire: { q: 0, r: 0 },
          },
        },
        {
          // The test: a slope of loose plates where the titan moved; the long cord, the fallen cairn's base, the Roller.
          kind: 'fight',
          id: 'fallen',
          mode: 'fallen',
          place: 'looseSlope',
          budgetBp: 9000,
          goal: { kind: 'fallen' },
          npcs: [{ id: 'sela', does: 'follow', hits: 10, vital: false, safe: true, follow: 1.4 }],
          says: [
            { on: 'start', who: 'sela', line: 'l0', delay: sec(2) },
            { on: 'cue', cue: 'long', who: 'sela', line: 'l1', delay: sec(1) },
            { on: 'cue', cue: 'roller', who: 'sela', line: 'l2' },
            { on: 'cue', cue: 'stun1', who: 'sela', line: 'l3' },
            { on: 'cue', cue: 'wrong1', who: 'sela', line: 'l4' },
          ],
          trial: true,
        },
        {
          // On the fallen cairn's base, a new red cord with a knot that isn't Aster's.
          kind: 'scene',
          id: 'close',
          lines: [ln('l1', 'narrator'), ln('l2', 'sela'), ln('l3', 'sela')],
          cine: {
            place: 'fallenBase',
            sky: 'dusk',
            cam: [
              { from: [-2.2, 4.0, 2.2], at: [0.2, -0.4, 0.4], ms: 8000 },
              { from: [-0.4, 1.4, 0.8], at: [0, -0.1, 0.3], ms: 9000 },
            ],
            figures: [
              { who: 'sela', q: 0, r: 1, facing: 4.6, pose: 'kneel' },
              { who: 'hero', q: 1, r: 1, facing: 4.0, pose: 'stand' },
            ],
          },
        },
        {
          // That night, at the table: Orla knows the knot.
          kind: 'scene',
          id: 'night',
          lines: [ln('l1', 'orla'), ln('l2', 'orla'), ln('l3', 'narrator')],
          cine: table(
            'night',
            [
              { from: [3.6, 0.6, 2.2], at: [1.2, -2.0, 0.5], ms: 9000 },
              { from: [-1.8, 0.0, 1.9], at: [1.6, -2.6, 0.9], ms: 9000 },
            ],
            [
              { who: 'orla', q: 2, r: -3, facing: 2.0, pose: 'stand' },
              { who: 'sela', q: 3, r: -2, facing: 3.0, pose: 'sit' },
              { who: 'hero', q: 0, r: -1, facing: 5.0, pose: 'stand' },
            ],
          ),
        },
      ],
    },
    {
      id: 'flank',
      closedBy: 'orla',
      piece: 'ropeRoll',
      acts: [
        {
          // The flank's wall seen from the sea: hundreds of metres of black rock, nests like stains, the foam below; an
          // old rope hanging cut from a crack; beside it six new red ropes going down.
          kind: 'scene',
          id: 'cine',
          lines: [],
          cine: {
            video: 'senda-ranger-2',
            place: 'flankSea',
            sky: 'dusk',
            // (From out at sea, low over the swell, looking up the wall: the sea is far under the board.)
            cam: [
              { from: [-10, 34, -26], at: [-2, 0, -12], ms: 6000 },
              { from: [-4, 28, -25], at: [3, -4, -6], ms: 6000 },
              { from: [3, 20, -15], at: [8, -8, -2], ms: 5000 },
            ],
          },
        },
        {
          kind: 'scene',
          id: 'open',
          lines: [ln('l1', 'orla'), ln('l2', 'orla'), ln('l3', 'sela'), ln('l4', 'orla'), ln('l5', 'orla', { notFlag: '@orla.ayuda' })],
          cine: table(
            'night',
            [
              { from: [1.0, 3.2, 3.0], at: [1.3, -1.9, 0.4], ms: 8000 },
              { from: [-2.0, -0.4, 1.8], at: [1.6, -2.6, 0.9], ms: 9000 },
              { from: [3.6, 0.4, 1.7], at: [1.3, -1.9, 0.6], ms: 9000 },
            ],
            [
              { who: 'orla', q: 2, r: -3, facing: 2.0, pose: 'stand' },
              { who: 'sela', q: 3, r: -2, facing: 3.0, pose: 'stand' },
              { who: 'hero', q: 0, r: -1, facing: 5.0, pose: 'stand' },
            ],
          ),
        },
        {
          // The top cornice in the sea wind: soot crows' nests in the cracks, shriekers in the deep ones; a hundred
          // paces off, Galo's crew going down their red ropes. Sela drives the first anchor, facing the sea.
          kind: 'fight',
          id: 'anchor',
          mode: 'cliffEdge',
          place: 'cliffTop',
          budgetBp: 10000,
          goal: { kind: 'cliffEdge' },
          npcs: [{ id: 'sela', does: 'wait', hits: 10, at: { q: 0, r: 2 } }],
          says: [
            { on: 'start', who: 'sela', line: 'l0', delay: sec(2) },
            { on: 'cue', cue: 'crew', who: 'sela', line: 'l1' },
            { on: 'cue', cue: 'shriek1', who: 'sela', line: 'l2' },
            { on: 'cue', cue: 'half', who: 'sela', line: 'l3' },
            { on: 'cue', cue: 'hurt', who: 'sela', line: 'l4' },
          ],
        },
        {
          // The wall itself, seen from the front, with its ledges: down ledge by ledge. Dusk, the red sun on the sea
          // lighting the wall from the side; Galo's crew going down beside, and the crows they scare come this way.
          kind: 'fight',
          id: 'wall',
          mode: 'cliffWall',
          place: 'cliffWall',
          budgetBp: 10000,
          goal: { kind: 'cliffWall' },
          npcs: [{ id: 'sela', does: 'follow', hits: 10, vital: false, safe: true, follow: 1.2 }],
          says: [
            { on: 'start', who: 'sela', line: 'l0', delay: sec(2) },
            { on: 'cue', cue: 'hollow1', who: 'sela', line: 'l1' },
            { on: 'cue', cue: 'firm1', who: 'sela', line: 'l2', delay: sec(1) },
            { on: 'cue', cue: 'crumb1', who: 'sela', line: 'l3' },
            { on: 'cue', cue: 'fall1', who: 'sela', line: 'l4' },
            { on: 'cue', cue: 'crew', who: 'sela', line: 'l5' },
          ],
        },
        {
          kind: 'fire',
          id: 'fire',
          with: ['sela'],
          questions: [
            { id: 'page', lines: [ln('l1', 'aster'), ln('l2', 'sela')], set: ['askedPage'] },
            { id: 'galo', lines: [ln('l1', 'sela'), ln('l2', 'sela')], set: ['askedGalo'] },
            { id: 'fear', lines: [ln('l1', 'sela')], set: ['askedFear'] },
          ],
          cine: {
            place: 'asterLedge',
            sky: 'night',
            cam: [
              { from: [2.4, 3.0, 2.0], at: [-0.3, -0.4, 0.4], ms: 9000 },
              { from: [-1.6, 2.6, 1.5], at: [0.1, -0.5, 0.45], ms: 9000 },
            ],
            figures: [
              { who: 'sela', q: -1, r: 1, facing: 5.4, pose: 'sit' },
              { who: 'hero', q: 1, r: 0, facing: 2.6, pose: 'sit' },
            ],
            fire: { q: 0, r: 0 },
          },
        },
        {
          // The test: the night on the wall. The next ledge further than the rope; a crack that crumbles (8 s with
          // weight), and every rope shot sounds: the shriekers come down to the last sound, and their mother shrieks.
          kind: 'fight',
          id: 'night',
          mode: 'cliffNight',
          place: 'cliffNight',
          night: true,
          budgetBp: 10000,
          goal: { kind: 'cliffNight' },
          npcs: [{ id: 'sela', does: 'follow', hits: 10, follow: 1.2 }],
          says: [
            { on: 'start', who: 'sela', line: 'l0', delay: sec(2) },
            { on: 'cue', cue: 'shriek1', who: 'sela', line: 'l1' },
            { on: 'cue', cue: 'stone1', who: 'sela', line: 'l2' },
            { on: 'cue', cue: 'mother', who: 'sela', line: 'l3' },
            { on: 'cue', cue: 'crumb1', who: 'sela', line: 'l4' },
            { on: 'cue', cue: 'voices', who: 'sela', line: 'l5' },
          ],
          trial: true,
        },
        {
          // On the last ledge Sela's anchor jumps out of the rock; she drops a metre, two. A hand out of the dark, off a
          // red rope, takes her by the harness. An old man pulls her up without a word and goes on down.
          kind: 'scene',
          id: 'grab',
          lines: [ln('l1', 'narrator'), ln('l2', 'sela'), ln('l3', 'narrator')],
          cine: {
            place: 'cliffGrab',
            sky: 'night',
            cam: [
              { from: [-1.8, 2.6, 1.6], at: [0.3, -0.6, 0.8], ms: 7000 },
              { from: [0.8, 1.2, 1.1], at: [0.4, -0.5, 1.0], ms: 8000 },
            ],
            figures: [
              { who: 'sela', q: 0, r: 0, facing: 4.5, pose: 'kneel' },
              { who: 'oldman', q: 1, r: -1, facing: 2.2, pose: 'stand' },
              { who: 'hero', q: -1, r: 1, facing: 5.2, pose: 'stand' },
            ],
          },
        },
        {
          // At the cliff's foot, a fire: six young walkers round it and an old man in a red cape, who stands up.
          kind: 'scene',
          id: 'close',
          lines: [ln('l1', 'narrator'), ln('l2', 'sela'), ln('l3', 'oldman'), ln('l4', 'sela'), ln('l5', 'galo'), ln('l6', 'narrator')],
          cine: {
            place: 'cliffFoot',
            sky: 'night',
            cam: [
              { from: [-2.6, 3.6, 2.4], at: [0.4, -0.2, 0.4], ms: 9000 },
              { from: [2.2, 1.0, 1.3], at: [0.6, -1.0, 0.9], ms: 9000 },
              { from: [-0.6, 1.6, 1.2], at: [-0.4, 0.4, 0.8], ms: 9000 },
            ],
            figures: [
              { who: 'galo', q: 1, r: -1, facing: 2.0, pose: 'stand' },
              { who: 'sela', q: -1, r: 1, facing: 5.2, pose: 'stand' },
              { who: 'hero', q: -2, r: 1, facing: 5.6, pose: 'stand' },
              { who: 'crew1', q: 2, r: 0, facing: 3.2, pose: 'sit' },
              { who: 'crew2', q: 0, r: -2, facing: 1.4, pose: 'sit' },
              { who: 'crew3', q: 2, r: -2, facing: 2.4, pose: 'sit' },
            ],
            fire: { q: 1, r: 0 },
          },
        },
      ],
    },
  ],
};
